import { useMemo, useState } from "react"
import { createFileRoute, Link } from "@tanstack/react-router"
import { useQuery } from "convex/react"
import { api } from "../../convex/_generated/api"
import { Input } from "@/components/ui/input"
import { Skeleton } from "@/components/ui/skeleton"
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs"
import Footer from "@/components/footer-1"
import { MapPin } from "lucide-react"
import { cn } from "@/lib/utils"
import { formatCardDateTime } from "@/lib/dates"
import { EmptyState } from "@/components/empty-state"

export const Route = createFileRoute("/hackathons/")({
  component: HackathonsPage,
})

type FilterMode = "upcoming" | "past" | "all"

function HackathonsPage() {
  const hackathons = useQuery(api.hackathons.listPublic)
  const profile = useQuery(api.profiles.me)
  const myRegs = useQuery(
    api.hackathons.listMyRegistrations,
    profile?.role === "seeker" ? {} : "skip",
  )
  const [search, setSearch] = useState("")
  const [mode, setMode] = useState<FilterMode>("upcoming")

  const appliedIds = useMemo(() => {
    const set = new Set<string>()
    for (const r of myRegs ?? []) set.add(r.hackathonId)
    return set
  }, [myRegs])

  const filteredHackathons = useMemo(() => {
    if (!hackathons) return []
    const now = Date.now()
    const q = search.trim().toLowerCase()
    const filtered = hackathons.filter((h) => {
      if (mode === "upcoming" && h.endsAt < now) return false
      if (mode === "past" && h.endsAt >= now) return false
      if (!q) return true
      return (
        h.title.toLowerCase().includes(q) ||
        h.employerName.toLowerCase().includes(q) ||
        h.venue.toLowerCase().includes(q) ||
        h.city.toLowerCase().includes(q)
      )
    })
    return [...filtered].sort((a, b) =>
      mode === "upcoming" ? a.startsAt - b.startsAt : b.startsAt - a.startsAt,
    )
  }, [hackathons, search, mode])

  const hasHackathons = (hackathons?.length ?? 0) > 0

  const clearFilters = () => {
    setSearch("")
    setMode("all")
  }

  return (
    <div className="mkt-page home-wf">
      <div className="mkt-page-shell">
        <h1 className="mkt-page-title">Hackathons</h1>
        <p className="mkt-page-sub">
          Browse free. Sign up costs nothing — applying needs ₹2,500/year
          access.{" "}
          <Link
            to="/jobs/browse"
            className="font-bold text-[color:var(--wf-lime-text)] underline-offset-4 hover:underline"
          >
            Browse open roles →
          </Link>
        </p>

        {hackathons === undefined ? (
          <div className="mt-8 space-y-4">
            <Skeleton className="h-36 w-full rounded-[1rem]" />
            <Skeleton className="h-36 w-full rounded-[1rem]" />
          </div>
        ) : !hasHackathons ? (
          <p className="mt-8 text-[color:var(--wf-mute)]">
            No published hackathons yet.
          </p>
        ) : (
          <>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Tabs
                value={mode}
                onValueChange={(value) => setMode(value as FilterMode)}
              >
                <TabsList>
                  <TabsTrigger value="upcoming">Upcoming</TabsTrigger>
                  <TabsTrigger value="past">Past</TabsTrigger>
                  <TabsTrigger value="all">All</TabsTrigger>
                </TabsList>
              </Tabs>
              <Input
                placeholder="Search title, host, venue, city…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="max-w-xs"
              />
            </div>

            <p className="mt-4 text-sm font-medium text-[color:var(--wf-mute)]">
              {filteredHackathons.length} event
              {filteredHackathons.length === 1 ? "" : "s"}
            </p>

            <div className="mt-4 flex flex-col gap-4">
              {filteredHackathons.length === 0 ? (
                <EmptyState
                  className="rounded-[1rem]"
                  title="No hackathons match your filters."
                  action={{
                    label: mode === "all" ? "Clear search" : "Show all events",
                    onClick: clearFilters,
                  }}
                />
              ) : (
                filteredHackathons.map((h) => {
                  const now = Date.now()
                  const ended = h.endsAt < now
                  const applied = appliedIds.has(h._id)
                  const full =
                    h.capacity !== undefined &&
                    h.registrationCount >= h.capacity
                  let chipLabel = profile?.role === "seeker" ? "Apply" : "Open"
                  let chipClass = "mkt-event-card__chip--apply"
                  if (applied) {
                    chipLabel = "Applied"
                    chipClass = "mkt-event-card__chip--ok"
                  } else if (ended) {
                    chipLabel = "Ended"
                    chipClass = "mkt-event-card__chip--mute"
                  } else if (full) {
                    chipLabel = "Full"
                    chipClass = "mkt-event-card__chip--mute"
                  }

                  return (
                    <Link
                      key={h._id}
                      to="/hackathons/$hackathonId"
                      params={{ hackathonId: h._id }}
                      className="mkt-event-card"
                    >
                      <div className="mkt-event-card__body">
                        <p className="mkt-event-card__when">
                          {formatCardDateTime(h.startsAt)}
                        </p>
                        <h2 className="mkt-event-card__title">{h.title}</h2>
                        <p className="mkt-event-card__host">
                          <span className="mkt-event-card__mark" aria-hidden>
                            {h.employerName.charAt(0).toUpperCase()}
                          </span>
                          <span>By {h.employerName}</span>
                        </p>
                        <p className="mkt-event-card__place">
                          <MapPin
                            className="size-3.5 shrink-0 opacity-70"
                            aria-hidden
                          />
                          <span>
                            {h.venue}, {h.city}
                          </span>
                        </p>
                        <div className="mkt-event-card__foot">
                          <span
                            className={cn("mkt-event-card__chip", chipClass)}
                          >
                            {chipLabel}
                          </span>
                          {h.registrationCount > 0 && (
                            <span className="mkt-event-card__count">
                              {h.registrationCount} applied
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="mkt-event-card__media" aria-hidden>
                        {h.imageUrl ? (
                          <img src={h.imageUrl} alt="" />
                        ) : (
                          <div className="mkt-event-card__media-fallback">
                            {h.title.charAt(0).toUpperCase()}
                          </div>
                        )}
                      </div>
                    </Link>
                  )
                })
              )}
            </div>
          </>
        )}
      </div>
      <Footer />
    </div>
  )
}
